import { put, call, takeLatest, all, select } from "redux-saga/effects"
import { httpSuccess, request } from "lib/request"
import config from "react-global-configuration"
import clientActions from "client/services/actions"
import { EmailCredential, ResponseAdmin, ResponsePayload } from "types/payload"
import { Admin } from "types/entity"
import { OwnerInfoDraft } from "types/store"

import { ADMIN_BILLING_REQUEST, ADMIN_LOGIN_REQUEST, ADMIN_LOGOUT, ADMIN_OWNER_INFO_UPDATE_REQUEST } from "./constants"
import {
  adminBillingError,
  adminBillingSuccess,
  adminLoginError,
  adminLoginSuccess,
  adminOwnerInfoUpdateError,
  adminOwnerInfoUpdateIncomplete,
  adminOwnerInfoUpdateSuccess,
} from "./actions"
import { Action, GFlow, GWatcher, Message } from "types/redux"
import configurationActions from "client/config/actions"
import { languages } from "locales/languages"
/*
 *  API REQUESTS
 */
async function adminLoginApi(payload?: {
  email: string
  password: string
}): Promise<ResponseAdmin> {
  return request(config.get("admin.login"), {
    method: "POST",
    payload,
  })
}

async function adminLogoutApi(): Promise<ResponsePayload> {
  return request(config.get("admin.login"), { method: "POST" })
}

async function adminBillingApi(payload: { rpps?: string, finess?: string, owner_firstname?: string, owner_lastname?: string }): Promise<ResponseAdmin> {
  return request(config.get("admin.billing"), {
    method: "POST",
    payload,
  })
}

async function adminMssApi(payload: { mss_email?: string, mss_opt_in?: boolean }): Promise<ResponseAdmin> {
  return request(config.get("admin.mss"), {
    method: "PATCH",
    payload,
  })
}

// Same RPPS verification service as the carer and TLE pharmacist flows
async function rppsIdentityApi(
  rpps: string
): Promise<{ status: string; data?: { firstname?: string; lastname?: string } }> {
  return request(config.get("admin.rpps"), {
    method: "POST",
    payload: { rpps },
  })
}

/*
 *  FLOWS
 */
function* logoutFlow(): GFlow<ResponsePayload> {
  yield call(adminLogoutApi)
  yield put(clientActions.reset)
}

function* adminFlow({
  payload,
}: Action<EmailCredential>): GFlow<ResponseAdmin | Action<Message>> {
  try {
    const response: ResponseAdmin = yield adminLoginApi(payload)
    if (response.pharmacy?.rpps) {
      localStorage.setItem("pharmacist_rpps", response.pharmacy.rpps)
    }
    if (response.pharmacy?.finess) {
      localStorage.setItem("pharmacist_finess", response.pharmacy.finess)
    }
    sessionStorage.removeItem("terminal")
    yield all([
      put(adminLoginSuccess("Administrateur connecté")),
      put(configurationActions.request()),
      put(clientActions.setAdmin(response.pharmacy)),
    ])
  } catch (error) {
    console.error(error, {route: config.get("admin.login")})
    yield put(adminLoginError(error as string))
  }
}

function* adminBillingFlow({
  payload,
}: Action<{ rpps: string, finess: string, owner_firstname: string, owner_lastname: string }>): GFlow<ResponseAdmin | Action<Message>> {
  try {
    const response: ResponseAdmin = yield adminBillingApi(payload)
    const rpps = response?.pharmacy?.rpps || payload.rpps
    const finess = response?.pharmacy?.finess || payload.finess
    const owner_firstname = response?.pharmacy?.owner_firstname || payload.owner_firstname
    const owner_lastname = response?.pharmacy?.owner_lastname || payload.owner_lastname
    localStorage.setItem("pharmacist_rpps", rpps)
    localStorage.setItem("pharmacist_finess", finess)
    const currentAdmin = yield select(({ client }) => client.admin)
    yield all([
      put(clientActions.setAdmin({ ...currentAdmin, rpps, finess, owner_firstname, owner_lastname })),
      put(adminBillingSuccess(languages.pharmacistBillingSuccess)),
    ])
  } catch (error) {
    console.error(error, { route: config.get("admin.billing") })
    const text =
      typeof error === "string"
        ? error
        : (error as Error)?.message || languages.pharmacistBillingError
    yield put(adminBillingError(text))
  }
}

/*
 * Updates the MSS preference of the pharmacy (address and opt-in), which has its own route.
 */
function* mssUpdateFlow(draft: OwnerInfoDraft, currentAdmin: Admin | undefined): GFlow<unknown> {
  const payload = {
    ...(draft.mss_email !== undefined ? { mss_email: draft.mss_email } : {}),
    ...(draft.mss_opt_in !== undefined ? { mss_opt_in: draft.mss_opt_in } : {}),
  }
  try {
    const response: ResponseAdmin = yield call(adminMssApi, payload)
    // A 404 resolves like a success in request(): check the status before trusting the body
    if (!httpSuccess(response)) throw languages.ownerInfoMssNotSaved
    const saved = response.pharmacy
    // Only trust the preference once the API echoes it back as stored
    if (draft.mss_email !== undefined && (saved?.mss_email ?? "") !== draft.mss_email) {
      yield put(adminOwnerInfoUpdateError(languages.ownerInfoMssNotSaved))
      return
    }
    if (draft.mss_opt_in !== undefined && saved?.mss_opt_in !== draft.mss_opt_in) {
      yield put(adminOwnerInfoUpdateError(languages.ownerInfoMssOptOutNotSaved))
      return
    }
    yield put(
      clientActions.setAdmin({
        ...(currentAdmin as Admin),
        mss_email: saved?.mss_email ?? undefined,
        mss_opt_in: saved?.mss_opt_in ?? currentAdmin?.mss_opt_in,
      }),
    )
    yield put(adminOwnerInfoUpdateSuccess(languages.ownerInfoSaved))
  } catch (error) {
    console.error(error, { route: config.get("admin.mss") })
    const text =
      typeof error === "string"
        ? error
        : (error as Error)?.message || languages.ownerInfoMssNotSaved
    yield put(adminOwnerInfoUpdateError(text))
  }
}

/*
 * Updates one piece of the pharmacy owner's billing identity (RPPS or FINESS), or the MSS
 * preference of the pharmacy. For a new owner (RPPS change) the whole billing record is sent,
 * with the owner names from the RPPS directory.
 */
function* ownerInfoUpdateFlow({ payload }: Action<OwnerInfoDraft>): GFlow<unknown> {
  const draft: OwnerInfoDraft = { ...payload }
  const currentAdmin: Admin | undefined = yield select(({ client }) => client.admin)

  const mssOnly =
    !draft.rpps && draft.finess === undefined && (draft.mss_email !== undefined || draft.mss_opt_in !== undefined)
  if (mssOnly) {
    yield call(mssUpdateFlow, draft, currentAdmin)
    return
  }

  const rpps = draft.rpps ?? currentAdmin?.rpps ?? undefined
  const finess = draft.finess ?? currentAdmin?.finess ?? undefined

  // Owner names belong to the RPPS: only a new RPPS brings (and may need to look up) its own names
  let owner_firstname = draft.rpps ? draft.owner_firstname : currentAdmin?.owner_firstname
  let owner_lastname = draft.rpps ? draft.owner_lastname : currentAdmin?.owner_lastname

  if (draft.rpps && !owner_firstname && !owner_lastname) {
    try {
      const identity = yield call(rppsIdentityApi, rpps)
      if (identity?.status !== "ok" || !identity?.data) throw identity
      owner_firstname = identity.data.firstname ?? ""
      owner_lastname = identity.data.lastname ?? ""
    } catch (error) {
      console.error(error, { route: config.get("admin.rpps") })
      yield put(adminOwnerInfoUpdateError(languages.ownerInfoRppsNotRecognized))
      return
    }
  }

  const validatedDraft: OwnerInfoDraft = draft.rpps
    ? { ...draft, owner_firstname, owner_lastname }
    : draft
  if (!rpps) {
    yield put(adminOwnerInfoUpdateIncomplete({ missing: "rpps", draft: validatedDraft }))
    return
  }
  if (!finess) {
    yield put(adminOwnerInfoUpdateIncomplete({ missing: "finess", draft: validatedDraft }))
    return
  }

  // Only an RPPS change sends the whole record (the API then versions the billing). Other saves
  // send just the changed field, so values loaded earlier can never overwrite newer ones.
  const billingPayload = draft.rpps
    ? { rpps, finess, owner_firstname: owner_firstname ?? "", owner_lastname: owner_lastname ?? "" }
    : { ...(draft.finess !== undefined ? { finess: draft.finess } : {}) }
  try {
    const response: ResponseAdmin = yield call(adminBillingApi, billingPayload)
    // A 404 resolves like a success in request(): check the status before trusting the body
    if (!httpSuccess(response)) throw languages.pharmacistBillingError
    const saved = response.pharmacy
    const nextAdmin: Admin = {
      ...(currentAdmin as Admin),
      rpps: saved?.rpps || rpps,
      finess: saved?.finess || finess,
      owner_firstname: saved?.owner_firstname ?? owner_firstname,
      owner_lastname: saved?.owner_lastname ?? owner_lastname,
      // The MSS preference is stored with the billing: the API returns the current one, empty for a new owner
      mss_email: saved?.mss_email ?? undefined,
      mss_opt_in: saved?.mss_opt_in ?? null,
    }
    localStorage.setItem("pharmacist_rpps", nextAdmin.rpps as string)
    localStorage.setItem("pharmacist_finess", nextAdmin.finess as string)
    yield put(clientActions.setAdmin(nextAdmin))
    yield put(adminOwnerInfoUpdateSuccess(languages.ownerInfoSaved))
  } catch (error) {
    console.error(error, { route: config.get("admin.billing") })
    // The API answers 434 when a new owner's RPPS is unknown to the directory; the shared error
    // mapping turns that code into a login message, so name the RPPS instead
    const text =
      error === languages.mismatchingErrorForm
        ? languages.ownerInfoRppsNotRecognized
        : typeof error === "string"
          ? error
          : (error as Error)?.message || languages.pharmacistBillingError
    yield put(adminOwnerInfoUpdateError(text))
  }
}

function* adminWatcher(): GWatcher {
  yield takeLatest(ADMIN_LOGIN_REQUEST, adminFlow)
  yield takeLatest(ADMIN_LOGOUT, logoutFlow)
  yield takeLatest(ADMIN_BILLING_REQUEST, adminBillingFlow)
  yield takeLatest(ADMIN_OWNER_INFO_UPDATE_REQUEST, ownerInfoUpdateFlow)
}

export default adminWatcher
